import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Группы шаблона в шаге «5. Структура» нового запуска (спека v4 §11.2): галочки
 * групп (снять можно, добавить нельзя — [Р] Q5), своя ставка группы (только
 * если шаблон разрешает менять ставку при запуске, §3.5), сумма ABO группы
 * («как общий» по умолчанию, [Р] 27), ставки копий матрицей «группа × копия»
 * по плану сервера. Деньги итогом не считаются — итог даёт сервер (`SpendSummary`).
 *
 * Состояние — поле формы `groups`: снятые ключи (`off`), свои ставки и суммы в
 * форменном виде (крупные единицы валюты кабинета).
 */
import { useState } from 'react';
import { z } from 'zod';
import { Badge, Button, Checkbox, IconButton, InlineNotice, Label, SortSelect, TextLink, } from '@wit-support/ui';
import { ZodField, useZodFormContext } from '@wit-support/ui-forms';
import { currencyScale, majorToMinor, minorToMajor } from '@wit-support/ui-domain';
import { Info, TriangleAlert, X } from 'lucide-react';
import { formatMinor } from '../format.js';
import { FieldIssues, useDisplayContext } from '../registry/registry-form.js';
import { displayValue } from '../registry/value-display.js';
import { ADVANTAGE_AUDIENCE_KEY, GROUP_BID_KEY, groupAdvantage, groupAriaLabel, groupIssueText, groupListText, groupTitle, groupsCountText, isAudienceGroup, } from '../groups.js';
export const BUDGET_KEY = 'budget.dailyMinor';
export const NO_GROUPS_FORM = { off: [], bid: {}, budget: {} };
export const GROUPS_ALL_REMOVED_TEXT = 'Отметьте хотя бы одну группу шаблона';
export const GROUPS_SINGLE_TEXT = 'Одна группа шаблона — кампания 1-1-1';
export const GROUPS_OFF_LAUNCH_TEXT = 'Группы шаблона сейчас выключены — запуск по этому шаблону недоступен';
export const GROUPS_UNKNOWN_LAUNCH_TEXT = 'Не удалось проверить, включены ли группы — запуск по этому шаблону пока недоступен';
export const GROUPS_CBO_TEXT = 'При CBO бюджет общий на кампанию — Meta делит его сама';
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
/**
 * Схема поля `groups`: при группах шаблона и включённом флаге — хотя бы одна
 * отмечена; свои ставки 100–100 000 и суммы ≥ 100 (в minor units, те же
 * пределы, что у контракта); флаг выключен — запуск не проверяется.
 */
export function launchGroupsSchema(o) {
    return z
        .object({
        off: z.array(z.string()),
        bid: z.record(z.string(), z.number().optional()),
        budget: z.record(z.string(), z.number().optional()),
    })
        .optional()
        .superRefine((v, ctx) => {
        // /health ещё грузится — не ошибка (ревью SB-7 P2-2)
        if (o.groups.length === 0 || o.feature === 'pending')
            return;
        if (o.feature === 'unknown') {
            ctx.addIssue({ code: 'custom', message: GROUPS_UNKNOWN_LAUNCH_TEXT });
            return;
        }
        if (o.feature === 'off') {
            ctx.addIssue({ code: 'custom', message: GROUPS_OFF_LAUNCH_TEXT });
            return;
        }
        const f = v ?? NO_GROUPS_FORM;
        const on = o.groups.filter((g) => !f.off.includes(g.key));
        if (on.length === 0)
            ctx.addIssue({ code: 'custom', message: GROUPS_ALL_REMOVED_TEXT });
        for (const g of on) {
            const sends = o.sends?.() ?? { bid: true, budget: true };
            const bid = f.bid[g.key];
            if (sends.bid && isNum(bid)) {
                const minor = majorToMinor(bid, o.currency());
                if (minor < 100 || minor > 100_000) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['bid', g.key],
                        message: `Ставка — от ${formatMinor(100, o.currency())} до ${formatMinor(100_000, o.currency())}`,
                    });
                }
            }
            const budget = f.budget[g.key];
            if (sends.budget && isNum(budget) && majorToMinor(budget, o.currency()) < 100) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['budget', g.key],
                    message: `Бюджет группы — не меньше ${formatMinor(100, o.currency())}`,
                });
            }
        }
    });
}
/**
 * Форма → `adSetGroups` входа пачки ([Р] 48): `keys` — только если что-то
 * снято (нет = все), `edits` — только свои значения отмеченных групп; ставка —
 * если её можно менять при запуске, сумма — только при ABO. Нечего сказать —
 * поля нет (вход старого вида).
 */
export function launchGroupsInput(f, groups, o) {
    if (groups.length === 0 || !f)
        return undefined;
    const on = groups.filter((g) => !f.off.includes(g.key));
    const edits = {};
    for (const g of on) {
        const e = {};
        const bid = f.bid[g.key];
        if (o.bidEditable && o.bidVisible !== false && isNum(bid))
            e.bidMinor = majorToMinor(bid, o.currency);
        const budget = f.budget[g.key];
        if (o.abo && isNum(budget))
            e.dailyBudgetMinor = majorToMinor(budget, o.currency);
        if (Object.keys(e).length > 0)
            edits[g.key] = e;
    }
    const out = {};
    if (on.length !== groups.length)
        out.keys = on.map((g) => g.key);
    if (Object.keys(edits).length > 0)
        out.edits = edits;
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * Вход пачки (повтор, «Создать такую же») → форма: снятые — те, кого нет в
 * `keys`; новые группы шаблона (`newKeys`) — без галочки, кроме случая «в
 * шаблоне появились группы» (`appeared`, все отмечены, [Р] 100).
 */
export function launchGroupsFromInput(input, groups, o) {
    const keys = input?.keys;
    const off = o.appeared
        ? []
        : groups
            .filter((g) => (keys ? !keys.includes(g.key) : false) || (o.newKeys ?? []).includes(g.key))
            .map((g) => g.key);
    const bid = {};
    const budget = {};
    for (const [k, e] of Object.entries(input?.edits ?? {})) {
        if (isNum(e.bidMinor))
            bid[k] = minorToMajor(e.bidMinor, o.currency);
        if (isNum(e.dailyBudgetMinor))
            budget[k] = minorToMajor(e.dailyBudgetMinor, o.currency);
    }
    return { off, bid, budget };
}
/** Блок групп шаблона в шаге 5. */
export function LaunchGroups(props) {
    const { form } = useZodFormContext();
    const ctx = useDisplayContext(props.currency);
    const f = form.watch('groups') ?? NO_GROUPS_FORM;
    const [sort, setSort] = useState(null);
    const { groups, live, currency } = props;
    if (groups.length === 0)
        return null;
    // `formState.errors` (а не `getFieldState`) — подписка: ошибка появляется сразу после галочки
    const groupsError = form.formState.errors.groups;
    const formErr = typeof groupsError?.message === 'string' ? groupsError.message : null;
    if (props.feature === 'pending') {
        return (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", "data-testid": "launch-groups-pending", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u043C, \u0432\u043A\u043B\u044E\u0447\u0435\u043D\u044B \u043B\u0438 \u0433\u0440\u0443\u043F\u043F\u044B \u0448\u0430\u0431\u043B\u043E\u043D\u0430\u2026" }));
    }
    if (props.feature === 'unknown') {
        // не прячем молча (§10.4, §11.1; ревью SB-7 P2-3)
        return (_jsx(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "launch-groups-unknown", children: _jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between", children: [_jsx("span", { children: GROUPS_UNKNOWN_LAUNCH_TEXT }), props.onRetryFeature ? (_jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: props.onRetryFeature, children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" })) : null] }) }));
    }
    if (props.feature === 'off') {
        return (_jsx(InlineNotice, { variant: "danger", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "launch-groups-off", children: GROUPS_OFF_LAUNCH_TEXT }));
    }
    const abo = live['campaign.budgetLevel'] === 'ABO';
    const commonBudget = isNum(live[BUDGET_KEY]) ? live[BUDGET_KEY] : null;
    const commonBid = isNum(live[GROUP_BID_KEY]) ? live[GROUP_BID_KEY] : null;
    const strategyHasBid = props.bidDesc
        ? props.bidDesc.visibleWhen == null ||
            props.bidDesc.visibleWhen.in.some((x) => String(x) === String(live[props.bidDesc.visibleWhen.key]))
        : false;
    const selected = groups.filter((g) => !f.off.includes(g.key));
    const setF = (next) => form.setValue('groups', next, { shouldDirty: true, shouldValidate: true });
    const toggle = (key, on) => setF({ ...f, off: on ? f.off.filter((k) => k !== key) : [...f.off, key] });
    const ownBudgets = Object.values(f.budget).some(isNum);
    // «сняты все» видно сразу (до «Проверить»); схема формы при этом не пустит план
    const err = selected.length === 0 ? GROUPS_ALL_REMOVED_TEXT : formErr;
    const groupBid = (g) => {
        const own = f.bid[g.key];
        if (props.bidEditable && isNum(own))
            return majorToMinor(own, currency);
        return isNum(g.values[GROUP_BID_KEY]) ? g.values[GROUP_BID_KEY] : commonBid;
    };
    const groupBudget = (g) => {
        const own = f.budget[g.key];
        return isNum(own) ? majorToMinor(own, currency) : commonBudget;
    };
    const ordered = [...groups.keys()];
    if (sort) {
        const sign = sort.dir === 'asc' ? 1 : -1;
        const val = (i) => {
            const g = groups[i];
            return sort.key === 'label' ? g.label : sort.key === 'bid' ? groupBid(g) : groupBudget(g);
        };
        ordered.sort((a, b) => {
            const x = val(a);
            const y = val(b);
            if (x == null || y == null)
                return x == null ? (y == null ? a - b : 1) : -1;
            return ((typeof x === 'string' ? x.localeCompare(String(y)) : x - y) * sign || a - b);
        });
    }
    const matrix = copyBidMatrix(props.preview, groups);
    return (_jsxs("div", { className: "flex flex-col gap-3", "data-testid": "launch-groups", children: [_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-sm font-semibold", children: "\u0413\u0440\u0443\u043F\u043F\u044B \u0448\u0430\u0431\u043B\u043E\u043D\u0430 \u0432 \u043A\u0430\u0436\u0434\u043E\u0439 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438" }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: props.copies > 1
                            ? `Каждая копия повторяет все группы шаблона: ${groupsCountText(selected.length, props.copies)}`
                            : groupsCountText(selected.length, 1) })] }), _jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-end", children: [_jsx(SortSelect, { id: "launch-groups-sort", label: "\u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043F\u043E", className: "", sort: sort, onSortChange: setSort, options: [
                            { key: 'label', label: 'названию', defaultDirection: 'asc' },
                            ...(strategyHasBid ? [{ key: 'bid', label: 'ставке' }] : []),
                            ...(abo ? [{ key: 'budget', label: 'сумме в день' }] : []),
                        ] }), sort ? (_jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => setSort(null), children: "\u043A\u0430\u043A \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435" })) : null] }), _jsx("ul", { className: "flex flex-col gap-2", children: ordered.map((i) => {
                    const g = groups[i];
                    const on = !f.off.includes(g.key);
                    const adv = groupAdvantage(g.values, live[ADVANTAGE_AUDIENCE_KEY]);
                    const diffs = Object.entries(g.values)
                        .filter(([k]) => k !== ADVANTAGE_AUDIENCE_KEY && k !== GROUP_BID_KEY)
                        .map(([k, v]) => {
                        const d = ctxDesc(k);
                        return d ? `${d.label}: ${displayValue(d, v, ctx)}` : null;
                    })
                        .filter((x) => x != null);
                    const own = props.issues.filter((x) => (x.groupKey ?? x.details?.groupKey) === g.key);
                    const id = `launch-group-${g.key}`;
                    const isNew = (props.newKeys ?? []).includes(g.key);
                    return (_jsxs("li", { className: `flex flex-col gap-2 rounded-md border border-[var(--color-border-subtle)] p-3 ${on ? '' : 'opacity-60'}`, "data-testid": `launch-group-${g.key}`, "aria-label": groupAriaLabel(i, g.label), children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Checkbox, { id: id, checked: on, "aria-label": `Создавать группу шаблона ${g.label}`, onCheckedChange: (v) => toggle(g.key, v === true) }), _jsx(Label, { htmlFor: id, className: "font-medium", children: groupTitle(i, g.label) }), isNew ? _jsx(Badge, { variant: "info", children: "\u043D\u043E\u0432\u0430\u044F \u2014 \u043D\u0435 \u0431\u044B\u043B\u043E \u0432 \u043F\u0440\u043E\u0448\u043B\u043E\u043C \u0437\u0430\u043F\u0443\u0441\u043A\u0435" }) : null, !on ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u043D\u0435 \u0431\u0443\u0434\u0435\u0442 \u0441\u043E\u0437\u0434\u0430\u043D\u0430" })) : null] }), _jsxs("dl", { className: "grid grid-cols-1 gap-x-3 gap-y-1 text-sm sm:grid-cols-[10rem_1fr]", children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u041E\u0442\u043B\u0438\u0447\u0438\u044F" }), _jsx("dd", { className: "[overflow-wrap:anywhere]", children: diffs.length > 0 ? diffs.join(' · ') : 'как общее' }), _jsx("dt", { className: "text-[var(--color-text-muted)]", children: "Advantage+" }), _jsxs("dd", { children: [adv.on ? 'вкл' : 'выкл', adv.source === 'audience-default' ? ' (своя аудитория)' : ''] }), strategyHasBid ? (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0421\u0442\u0430\u0432\u043A\u0430" }), _jsx("dd", { children: _jsx(BidCell, { ...props, g: g, on: on, f: f, setF: setF }) })] })) : null, abo ? (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: "\u0412 \u0434\u0435\u043D\u044C" }), _jsx("dd", { children: _jsx(MoneyOwnCell, { name: `groups.budget.${g.key}`, label: "\u0412 \u0434\u0435\u043D\u044C", placeholder: commonBudget != null
                                                        ? `как общий (${formatMinor(commonBudget, currency)})`
                                                        : 'как общий', own: isNum(f.budget[g.key]), disabled: !on, currency: currency, onClear: () => {
                                                        const { [g.key]: _d, ...rest } = f.budget;
                                                        setF({ ...f, budget: rest });
                                                    } }) })] })) : null] }), matrix.get(g.key) && props.copies > 1 ? (_jsxs("span", { className: "text-xs tabular-nums [overflow-wrap:anywhere]", "data-testid": `copy-bids-${g.key}`, children: [groupTitle(i, g.label), " \u2014", ' ', matrix
                                        .get(g.key)
                                        .map((b) => formatMinor(b, currency))
                                        .join(' · ')] })) : null, _jsx(FieldIssues, { issues: own.map((x) => ({ ...x, message: groupIssueText(x, groups) })) })] }, g.key));
                }) }), _jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0421\u043D\u044F\u0442\u0430\u044F \u0433\u0440\u0443\u043F\u043F\u0430 \u0448\u0430\u0431\u043B\u043E\u043D\u0430 \u043D\u0435 \u0431\u0443\u0434\u0435\u0442 \u0441\u043E\u0437\u0434\u0430\u043D\u0430. \u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0433\u0440\u0443\u043F\u043F\u0443 \u043C\u043E\u0436\u043D\u043E \u0442\u043E\u043B\u044C\u043A\u043E \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435", ' ', _jsx(TextLink, { href: `/fb-launches/templates/${props.templateId}`, target: "_blank", rel: "noreferrer", children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u2197" })] }), abo && ownBudgets ? (_jsx(Button, { type: "button", size: "sm", variant: "secondary", className: "w-full sm:w-fit", onClick: () => setF({ ...f, budget: {} }), children: "\u0412\u0441\u0435\u043C \u0433\u0440\u0443\u043F\u043F\u0430\u043C \u2014 \u043A\u0430\u043A \u043E\u0431\u0449\u0438\u0439 \u0431\u044E\u0434\u0436\u0435\u0442" })) : null, !abo && ownBudgets ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "groups-cbo", children: GROUPS_CBO_TEXT })) : null, props.copies > 1 && !props.preview && strategyHasBid ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0421\u0442\u0430\u0432\u043A\u0443 \u043A\u0430\u0436\u0434\u043E\u0439 \u043A\u043E\u043F\u0438\u0438 \u043F\u043E\u043A\u0430\u0436\u0435\u0442 \u00AB\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C\u00BB." })) : null, err ? (_jsx("span", { role: "alert", className: "text-sm text-[var(--color-state-danger-fg)]", "data-testid": "launch-groups-error", children: err })) : selected.length === 1 ? (_jsx(InlineNotice, { variant: "info", icon: Info, as: "div", "data-testid": "launch-groups-single", children: GROUPS_SINGLE_TEXT })) : null] }));
    function ctxDesc(key) {
        return props.descs?.find((d) => d.key === key);
    }
}
function BidCell(props) {
    const { g, f, currency } = props;
    const tplBid = isNum(g.values[GROUP_BID_KEY]) ? g.values[GROUP_BID_KEY] : null;
    const common = isNum(props.live[GROUP_BID_KEY]) ? props.live[GROUP_BID_KEY] : null;
    const base = tplBid ?? common;
    if (!props.bidEditable || !props.bidDesc) {
        return (_jsxs("span", { children: [base != null ? formatMinor(base, currency) : 'не задана', " \u00B7 \u043D\u0435\u043B\u044C\u0437\u044F \u043C\u0435\u043D\u044F\u0442\u044C \u043F\u0440\u0438 \u0437\u0430\u043F\u0443\u0441\u043A\u0435"] }));
    }
    return (_jsx(MoneyOwnCell, { name: `groups.bid.${g.key}`, label: "\u0421\u0432\u043E\u044F \u0441\u0442\u0430\u0432\u043A\u0430", placeholder: base != null
            ? `${tplBid != null ? 'своя в шаблоне' : 'общая'} (${formatMinor(base, currency)})`
            : 'общая', own: isNum(f.bid[g.key]), disabled: !props.on, currency: currency, onClear: () => {
            const { [g.key]: _d, ...rest } = f.bid;
            props.setF({ ...f, bid: rest });
        } }));
}
/** Сумма/ставка «как общий / своя [×]» (UA В2): пусто — следует за общим, введённое — жирно с чипом. */
function MoneyOwnCell(props) {
    const digits = currencyScale(props.currency);
    const schema = z
        .number()
        .optional()
        .meta({
        label: props.currency ? `${props.label}, ${props.currency.toUpperCase()}` : props.label,
        widget: 'decimal',
        placeholder: props.placeholder || (digits > 0 ? `0,${'0'.repeat(digits)}` : '0'),
    });
    return (_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("div", { className: `w-40 ${props.own ? 'font-semibold' : ''}`, children: _jsx(ZodField, { name: props.name, schema: schema, ...(props.disabled ? { disabled: true } : {}) }) }), props.own ? (_jsxs(_Fragment, { children: [_jsx(Badge, { variant: "outline", children: "\u0441\u0432\u043E\u044F" }), _jsx(IconButton, { label: `Вернуть как общий: ${props.label}`, variant: "ghost", className: "min-h-8 min-w-8", onClick: props.onClear, children: _jsx(X, { "aria-hidden": true, className: "h-4 w-4" }) })] })) : null] }));
}
/**
 * Ставки копий по группам из плана сервера («① LAL23 — $21.69 · $21.99 ·
 * $22.29», UB Б-20): первая кампания плана, по возрастанию копии.
 */
export function copyBidMatrix(preview, groups) {
    const out = new Map();
    const first = preview?.launches[0];
    if (!first)
        return out;
    // копии — группы объявлений одной кампании (1-N-1) ИЛИ разные кампании
    // («кампания на каждый креатив»): берём кампании первого кабинета и первого
    // креатива, копию — `copyIndex` группы объявлений, иначе кампании (ревью SB-7 P2-5)
    const creativeOf = (l) => {
        const c = l.creatives?.[0] ?? l.adSets[0]?.ads[0]?.creative;
        return c ? `${c.kind}:${c.objectStoryId ?? ''}:${c.assetId ?? ''}` : '';
    };
    const key0 = creativeOf(first);
    const launches = preview.launches.filter((l) => l.platformAccountId === first.platformAccountId && creativeOf(l) === key0);
    for (const g of groups) {
        const byCopy = new Map();
        for (const l of launches) {
            for (const as of l.adSets) {
                if (as.group?.key !== g.key || as.bidAmountMinor == null)
                    continue;
                const copy = as.copyIndex != null && as.copyIndex > 0 ? as.copyIndex : l.copyIndex;
                if (!byCopy.has(copy))
                    byCopy.set(copy, as.bidAmountMinor);
            }
        }
        const bids = [...byCopy.entries()].sort((a, b) => a[0] - b[0]).map(([, b]) => b);
        if (bids.length > 0)
            out.set(g.key, bids);
    }
    return out;
}
/**
 * Строки шага 4 про группы (§11.2): поле, которое у групп своё, — «В группе ②
 * LAL45 своё значение — это изменение на неё не действует»; включённый при
 * запуске Advantage+ при аудиторных группах без явного значения — жёлтая
 * строка (UB Б-15). Сервер подтверждает предупреждением `group-overrides-launch-value`.
 */
export function LaunchFieldGroupsNote({ desc, groups, live, tplValue, }) {
    if (groups.length === 0)
        return null;
    if (desc.key === ADVANTAGE_AUDIENCE_KEY) {
        const changed = live[ADVANTAGE_AUDIENCE_KEY] === true && tplValue !== true;
        const keys = groups
            .filter((g) => isAudienceGroup(g.values) && typeof g.values[ADVANTAGE_AUDIENCE_KEY] !== 'boolean')
            .map((g) => g.key);
        if (!changed || keys.length === 0)
            return null;
        return (_jsxs(InlineNotice, { variant: "warning", bordered: true, icon: TriangleAlert, as: "div", "data-testid": "launch-adv-groups", children: ["\u0412\u044B \u0432\u043A\u043B\u044E\u0447\u0438\u043B\u0438 Advantage+, \u043D\u043E \u0432 \u0433\u0440\u0443\u043F\u043F\u0430\u0445 ", groupListText(keys, groups), " (\u0441\u0432\u043E\u044F \u0430\u0443\u0434\u0438\u0442\u043E\u0440\u0438\u044F) \u043E\u043D \u043E\u0441\u0442\u0430\u043D\u0435\u0442\u0441\u044F \u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D. \u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0432 \u043D\u0438\u0445 \u2014 \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435"] }));
    }
    const own = groups.filter((g) => desc.key in g.values).map((g) => g.key);
    if (desc.key === BUDGET_KEY && live['campaign.budgetLevel'] === 'ABO') {
        return (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0411\u044E\u0434\u0436\u0435\u0442 \u0433\u0440\u0443\u043F\u043F\u044B \u043E\u0431\u044A\u044F\u0432\u043B\u0435\u043D\u0438\u0439 \u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E \u2014 \u0443 \u043A\u0430\u0436\u0434\u043E\u0439 \u0433\u0440\u0443\u043F\u043F\u044B \u0448\u0430\u0431\u043B\u043E\u043D\u0430 \u043C\u043E\u0436\u043D\u043E \u0437\u0430\u0434\u0430\u0442\u044C \u0441\u0432\u043E\u044E \u0441\u0443\u043C\u043C\u0443 \u0432 \u0448\u0430\u0433\u0435 5." }));
    }
    if (own.length === 0)
        return null;
    return (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": `launch-groups-note-${desc.key}`, children: [own.length === 1 ? 'В группе' : 'В группах', " ", groupListText(own, groups), " \u0441\u0432\u043E\u0451 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435 \u2014 \u044D\u0442\u043E \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0435 \u043D\u0430 ", own.length === 1 ? 'неё' : 'них', " \u043D\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442"] }));
}
//# sourceMappingURL=launch-groups.js.map